import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

import Button from '../../../components/ui/Button';

const PropertyDetails = ({ property, onClose }) => {
  const [activeTab, setActiveTab] = useState('basic');

  if (!property) {
    return (
      <div className="h-full flex flex-col items-center justify-center px-4 py-6">
        <Icon name="Building2" size={48} color="var(--color-muted-foreground)" className="mb-4" />
        <p className="text-sm text-muted-foreground text-center font-caption">
          Select a property from the table to view details
        </p>
      </div>
    );
  }

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: 'Info' },
    { id: 'tenancy', label: 'Tenancy History', icon: 'History' },
    { id: 'certificates', label: 'Certificates', icon: 'ShieldCheck' },
    { id: 'maintenance', label: 'Maintenance', icon: 'Wrench' },
    { id: 'documents', label: 'Documents', icon: 'FileText' }
  ];

  const certificates = [
    { type: 'Gas Safety', expiry: '15/08/2026', status: 'Valid', daysRemaining: 180 },
    { type: 'EPC', expiry: '22/11/2026', status: 'Valid', daysRemaining: 278 },
    { type: 'EICR', expiry: '10/03/2026', status: 'Expiring Soon', daysRemaining: 22 },
    { type: 'Legionella', expiry: '05/07/2026', status: 'Valid', daysRemaining: 139 }
  ];

  const maintenanceJobs = [
    { id: 'MJ-2401', title: 'Boiler Service', status: 'Completed', date: '12/01/2026', cost: 125 },
    { id: 'MJ-2398', title: 'Leaking Tap Repair', status: 'Completed', date: '08/01/2026', cost: 85 },
    { id: 'MJ-2387', title: 'Window Lock Replacement', status: 'In Progress', date: '28/12/2025', cost: 150 }
  ];

  const documents = [
    { name: 'Tenancy Agreement.pdf', size: '2.4 MB', uploaded: '15/06/2025', type: 'pdf' },
    { name: 'Inventory Report.pdf', size: '1.8 MB', uploaded: '15/06/2025', type: 'pdf' },
    { name: 'Gas Certificate.pdf', size: '856 KB', uploaded: '15/08/2025', type: 'pdf' },
    { name: 'Property Photos.zip', size: '12.3 MB', uploaded: '10/06/2025', type: 'zip' }
  ];

  const getCertificateColor = (status) => {
    const colors = {
      'Valid': 'var(--color-success)',
      'Expiring Soon': 'var(--color-warning)',
      'Expired': 'var(--color-error)'
    };
    return colors?.[status] || 'var(--color-muted-foreground)';
  };

  const getMaintenanceStatusColor = (status) => {
    const colors = {
      'Completed': 'var(--color-success)',
      'In Progress': 'var(--color-accent)',
      'Pending': 'var(--color-warning)'
    };
    return colors?.[status] || 'var(--color-muted-foreground)';
  };

  return (
    <div className="h-full flex flex-col bg-surface rounded-lg border border-border">
      <div className="px-4 py-4 border-b border-border flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground font-heading">{property?.propertyId}</h2>
          <p className="text-sm text-muted-foreground font-caption">{property?.address}</p>
        </div>
        <button
          onClick={onClose}
          className="h-8 w-8 rounded-lg bg-input border border-border flex items-center justify-center transition-smooth hover:bg-muted lg:hidden"
          aria-label="Close details"
        >
          <Icon name="X" size={16} color="var(--color-muted-foreground)" />
        </button>
      </div>
      <div className="flex border-b border-border overflow-x-auto">
        {tabs?.map((tab) => (
          <button
            key={tab?.id}
            onClick={() => setActiveTab(tab?.id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-smooth whitespace-nowrap font-caption ${
              activeTab === tab?.id
                ? 'text-accent border-b-2 border-accent' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name={tab?.icon} size={16} />
            <span>{tab?.label}</span>
          </button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {activeTab === 'basic' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-muted-foreground font-caption">Property Type</label>
                <p className="text-sm text-foreground font-medium mt-1">{property?.type}</p>
              </div>
              <div>
                <label className="text-xs text-muted-foreground font-caption">Bedrooms</label>
                <p className="text-sm text-foreground font-medium mt-1">{property?.bedrooms}</p>
              </div>
              <div>
                <label className="text-xs text-muted-foreground font-caption">Postcode</label>
                <p className="text-sm text-foreground font-medium mt-1 font-data">{property?.postcode}</p>
              </div>
              <div>
                <label className="text-xs text-muted-foreground font-caption">Monthly Rent</label>
                <p className="text-sm text-foreground font-medium mt-1 font-data">£{property?.monthlyRent?.toLocaleString()}</p>
              </div>
            </div>

            <div className="pt-4 border-t border-border">
              <h3 className="text-sm font-semibold text-foreground mb-3 font-heading">Landlord Details</h3>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-muted-foreground font-caption">Name</label>
                  <p className="text-sm text-foreground font-medium mt-1">{property?.landlord}</p>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground font-caption">Contact</label>
                  <p className="text-sm text-foreground font-medium mt-1 font-data">{property?.landlordContact}</p>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground font-caption">Email</label>
                  <p className="text-sm text-foreground font-medium mt-1 font-data">{property?.landlordEmail}</p>
                </div>
              </div>
            </div>

            {property?.tenant && (
              <div className="pt-4 border-t border-border">
                <h3 className="text-sm font-semibold text-foreground mb-3 font-heading">Current Tenant</h3>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-muted-foreground font-caption">Name</label>
                    <p className="text-sm text-foreground font-medium mt-1">{property?.tenant}</p>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground font-caption">Contact</label>
                    <p className="text-sm text-foreground font-medium mt-1 font-data">{property?.tenantContact}</p>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground font-caption">Tenancy Period</label>
                    <p className="text-sm text-foreground font-medium mt-1 font-data">
                      {property?.tenancyStart} - {property?.tenancyEnd}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'tenancy' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-foreground font-heading">Tenancy History</h3>
              <Button variant="outline" size="sm" iconName="Plus" iconPosition="left">
                Add Tenancy
              </Button>
            </div>
            <div className="space-y-3">
              <div className="p-4 rounded-lg bg-input border border-border">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-foreground">{property?.tenant}</span>
                  <span className="px-2 py-1 rounded text-xs font-medium font-caption" style={{ backgroundColor: 'rgba(42, 157, 143, 0.1)', color: 'var(--color-success)' }}>
                    Current
                  </span>
                </div>
                <p className="text-xs text-muted-foreground font-data">
                  {property?.tenancyStart} - {property?.tenancyEnd}
                </p>
                <p className="text-xs text-muted-foreground mt-1 font-data">
                  Monthly Rent: £{property?.monthlyRent?.toLocaleString()}
                </p>
              </div>
              <div className="p-4 rounded-lg bg-input border border-border">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-foreground">Michael Thompson</span>
                  <span className="px-2 py-1 rounded text-xs font-medium font-caption" style={{ backgroundColor: 'rgba(148, 163, 184, 0.1)', color: 'var(--color-muted-foreground)' }}>
                    Previous
                  </span>
                </div>
                <p className="text-xs text-muted-foreground font-data">15/06/2023 - 14/06/2025</p>
                <p className="text-xs text-muted-foreground mt-1 font-data">Monthly Rent: £1,450</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'certificates' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-foreground font-heading">Property Certificates</h3>
              <Button variant="outline" size="sm" iconName="Plus" iconPosition="left">
                Add Certificate
              </Button>
            </div>
            <div className="space-y-3">
              {certificates?.map((cert, index) => (
                <div key={index} className="p-4 rounded-lg bg-input border border-border">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Icon name="ShieldCheck" size={16} color={getCertificateColor(cert?.status)} />
                      <span className="text-sm font-medium text-foreground">{cert?.type}</span>
                    </div>
                    <span
                      className="px-2 py-1 rounded text-xs font-medium font-caption"
                      style={{
                        backgroundColor: `${getCertificateColor(cert?.status)}15`,
                        color: getCertificateColor(cert?.status)
                      }}
                    >
                      {cert?.status}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span className="font-data">Expires: {cert?.expiry}</span>
                    <span className="font-data">{cert?.daysRemaining} days remaining</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'maintenance' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-foreground font-heading">Maintenance Records</h3>
              <Button variant="outline" size="sm" iconName="Plus" iconPosition="left">
                New Job
              </Button>
            </div>
            <div className="space-y-3">
              {maintenanceJobs?.map((job) => (
                <div key={job?.id} className="p-4 rounded-lg bg-input border border-border">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-accent font-data">{job?.id}</span>
                      <span className="text-sm text-foreground">{job?.title}</span>
                    </div>
                    <span
                      className="px-2 py-1 rounded text-xs font-medium font-caption"
                      style={{
                        backgroundColor: `${getMaintenanceStatusColor(job?.status)}15`,
                        color: getMaintenanceStatusColor(job?.status)
                      }}
                    >
                      {job?.status}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span className="font-data">{job?.date}</span>
                    <span className="font-data">Cost: £{job?.cost}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'documents' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-foreground font-heading">Property Documents</h3>
              <Button variant="outline" size="sm" iconName="Upload" iconPosition="left">
                Upload
              </Button>
            </div>
            <div className="space-y-3">
              {documents?.map((doc, index) => (
                <div key={index} className="p-4 rounded-lg bg-input border border-border flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-accent/10 flex items-center justify-center">
                      <Icon name="FileText" size={20} color="var(--color-accent)" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">{doc?.name}</p>
                      <p className="text-xs text-muted-foreground font-data">
                        {doc?.size} • Uploaded {doc?.uploaded}
                      </p>
                    </div>
                  </div>
                  <button className="h-8 w-8 rounded-lg bg-input border border-border flex items-center justify-center transition-smooth hover:bg-muted">
                    <Icon name="Download" size={16} color="var(--color-muted-foreground)" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="px-4 py-4 border-t border-border flex gap-3">
        <Button variant="outline" fullWidth iconName="Edit" iconPosition="left">
          Edit Property
        </Button>
        <Button variant="default" fullWidth iconName="Eye" iconPosition="left">
          Full Details
        </Button>
      </div>
    </div>
  );
};

export default PropertyDetails;